import { useEffect, useRef } from "react";
import * as Y from "yjs";
import { REMOTE_ORIGIN } from "@/lib/useYjsRoom";

/**
 * useYjsEngineBridge — observes a shared Y.Doc and applies remote mutations
 * (transport + per-channel mute/volume/pan/solo) to the local audio engine.
 *
 * This is the read half of the CRDT retrofit; the write half is wired
 * directly into `engine.onCollabEvent` in the parent, which calls
 * `yjs.transact(...)` for mixer + transport mutations instead of `collab.send`.
 *
 * Loop-back: every observer checks `tx.origin === REMOTE_ORIGIN` before
 * touching the engine. Local mutations (`LOCAL_ORIGIN` from useYjsRoom.transact)
 * are silently ignored — the engine ALREADY has them.
 *
 * Tracked Y types:
 *   doc.getMap("transport") — { action: 'play'|'pause'|'stop'|'seek',
 *                                position?: number, tempo?: number, ts: number }
 *   doc.getMap("channels")  — Y.Map<string, Y.Map<{volume,pan,muted,solo}>>
 *                              outer keyed by stringified channel index 0..63.
 *
 * Why string keys: Y.Map keys are coerced to string anyway. Using stringified
 * indices makes the wire format human-debuggable when we log Yjs updates.
 */
export default function useYjsEngineBridge({ doc, ready, engine, setPlaying }) {
  // The engine + setPlaying refs sidestep `useEffect` rerunning on every
  // render — observers only need the live engine, not a fresh closure.
  const engineRef = useRef(engine);
  const setPlayingRef = useRef(setPlaying);
  useEffect(() => { engineRef.current = engine; }, [engine]);
  useEffect(() => { setPlayingRef.current = setPlaying; }, [setPlaying]);

  useEffect(() => {
    if (!ready || !doc) return undefined;
    const transport = doc.getMap("transport");
    const channels = doc.getMap("channels");
    const fx = doc.getMap("fx");
    const sidechain = doc.getMap("sidechain");

    // Track per-channel observers so we can unsubscribe cleanly.
    const channelObservers = new Map();
    // Per-channel sidechain observers — same lifecycle.
    const sidechainObservers = new Map();

    /** Apply a remote transport event. We mirror the logic in
     *  applyCollabEvent (legacy WS path) but read from the shared map. */
    function onTransportTx(_event, tx) {
      if (tx.origin !== REMOTE_ORIGIN) return;
      const action = transport.get("action");
      const pos = Number(transport.get("position") ?? 0);
      const eng = engineRef.current;
      if (!eng) return;
      eng._collabSilent = true;
      try {
        if (action === "play") { eng.seekTo(pos); eng.play(); setPlayingRef.current?.(true); }
        else if (action === "pause") { eng.pause(); setPlayingRef.current?.(false); }
        else if (action === "stop") { eng.stop(); setPlayingRef.current?.(false); }
        else if (action === "seek") { eng.seekTo(pos); }
      } finally { eng._collabSilent = false; }
    }

    /** Apply a remote per-channel field change. */
    function makeChannelObserver(idx) {
      return function onChannelTx(event, tx) {
        if (tx.origin !== REMOTE_ORIGIN) return;
        const chMap = channels.get(String(idx));
        if (!(chMap instanceof Y.Map)) return;
        const eng = engineRef.current;
        if (!eng) return;
        eng._collabSilent = true;
        try {
          event.changes.keys.forEach((_change, key) => {
            const value = chMap.get(key);
            if (key === "volume" && typeof value === "number") eng.setChannelVolume(idx, value);
            else if (key === "pan" && typeof value === "number") eng.setChannelPan(idx, value);
            else if (key === "muted") eng.setChannelMute(idx, !!value);
            else if (key === "solo") eng.setChannelSolo(idx, !!value);
          });
        } finally { eng._collabSilent = false; }
      };
    }

    /** Subscribe to one channel's Y.Map. Idempotent — calling twice is a noop. */
    function watchChannel(idx) {
      if (channelObservers.has(idx)) return;
      const chMap = channels.get(String(idx));
      if (!(chMap instanceof Y.Map)) return;
      const obs = makeChannelObserver(idx);
      chMap.observe(obs);
      channelObservers.set(idx, obs);
    }

    /** Re-sync all channels — used on initial mount and whenever a new channel
     *  Y.Map appears via the outer-map observer below. Also applies the
     *  current snapshot synchronously so a late-joiner instantly converges. */
    function syncAllChannels() {
      channels.forEach((chMap, key) => {
        const idx = Number(key);
        if (!Number.isFinite(idx)) return;
        watchChannel(idx);
        const eng = engineRef.current;
        if (!eng || !(chMap instanceof Y.Map)) return;
        eng._collabSilent = true;
        try {
          chMap.forEach((value, field) => {
            if (field === "volume" && typeof value === "number") eng.setChannelVolume(idx, value);
            else if (field === "pan" && typeof value === "number") eng.setChannelPan(idx, value);
            else if (field === "muted") eng.setChannelMute(idx, !!value);
            else if (field === "solo") eng.setChannelSolo(idx, !!value);
          });
        } finally { eng._collabSilent = false; }
      });
    }

    /** Outer-map observer — watches for new channels appearing in the doc
     *  (e.g. when a peer touches a channel we haven't subscribed to yet).
     *
     *  Critical: when a channel Y.Map is ADDED in the same Yjs transaction
     *  as its first value writes, the per-channel observer hasn't been
     *  attached yet so the inner fields fire BEFORE we're listening. We
     *  fix this by applying a one-shot snapshot here for every newly-added
     *  channel — same shape as syncAllChannels but scoped to one key. */
    function onChannelsKeyChange(event) {
      event.changes.keys.forEach((change, key) => {
        if (change.action === "add" || change.action === "update") {
          const idx = Number(key);
          if (!Number.isFinite(idx)) return;
          watchChannel(idx);
          // Apply the current state of this single channel immediately so
          // values written atomically with the channel's creation aren't
          // lost between observer-attach and the next mutation.
          const chMap = channels.get(key);
          const eng = engineRef.current;
          if (!eng || !(chMap instanceof Y.Map)) return;
          eng._collabSilent = true;
          try {
            chMap.forEach((value, field) => {
              if (field === "volume" && typeof value === "number") eng.setChannelVolume(idx, value);
              else if (field === "pan" && typeof value === "number") eng.setChannelPan(idx, value);
              else if (field === "muted") eng.setChannelMute(idx, !!value);
              else if (field === "solo") eng.setChannelSolo(idx, !!value);
              // `lockedBy` is intentionally not applied to the engine — it's
              // a UI-only field handled by useChannelLocks.
            });
          } finally { eng._collabSilent = false; }
        }
      });
    }

    transport.observe(onTransportTx);
    channels.observe(onChannelsKeyChange);

    // ─── FX param flat-key Y.Map ────────────────────────────────────────
    // Keys: "channelIdx:effectId:paramName" → value (number).
    // Flat keying instead of nested maps because FX params are touched in
    // bursts (knob drags) and a single observer firing on a flat Y.Map is
    // cheaper than chained per-effect observer trees.
    function onFxKeyChange(event, tx) {
      if (tx.origin !== REMOTE_ORIGIN) return;
      const eng = engineRef.current;
      if (!eng) return;
      eng._collabSilent = true;
      try {
        event.changes.keys.forEach((_change, key) => {
          const parts = String(key).split(":");
          if (parts.length !== 3) return;
          const [chStr, effect, param] = parts;
          const ch = Number(chStr);
          const value = fx.get(key);
          if (Number.isFinite(ch) && effect && param && typeof value === "number") {
            eng.setChannelEffectParam(ch, effect, param, value);
          }
        });
      } finally { eng._collabSilent = false; }
    }
    function syncAllFx() {
      const eng = engineRef.current;
      if (!eng) return;
      eng._collabSilent = true;
      try {
        fx.forEach((value, key) => {
          const parts = String(key).split(":");
          if (parts.length !== 3) return;
          const [chStr, effect, param] = parts;
          const ch = Number(chStr);
          if (Number.isFinite(ch) && effect && param && typeof value === "number") {
            eng.setChannelEffectParam(ch, effect, param, value);
          }
        });
      } finally { eng._collabSilent = false; }
    }
    fx.observe(onFxKeyChange);

    // ─── Sidechain Y.Map<channelIdx, Y.Map<{...patch}>> ─────────────────
    // Nested map (mirroring `channels`) because sidechain settings update as
    // multi-field patches and we want efficient per-channel observers.
    function makeSidechainObserver(idx) {
      return function onSidechainTx(_event, tx) {
        if (tx.origin !== REMOTE_ORIGIN) return;
        const sMap = sidechain.get(String(idx));
        if (!(sMap instanceof Y.Map)) return;
        const eng = engineRef.current;
        if (!eng) return;
        const patch = {};
        sMap.forEach((value, field) => {
          if (field === "mode" || field === "keyChannel") patch[field] = value;
          else if (typeof value === "number") patch[field] = value;
        });
        eng._collabSilent = true;
        try { eng.setSidechain(idx, patch); }
        finally { eng._collabSilent = false; }
      };
    }
    function watchSidechain(idx) {
      if (sidechainObservers.has(idx)) return;
      const sMap = sidechain.get(String(idx));
      if (!(sMap instanceof Y.Map)) return;
      const obs = makeSidechainObserver(idx);
      sMap.observe(obs);
      sidechainObservers.set(idx, obs);
    }
    function syncAllSidechain() {
      sidechain.forEach((_sMap, key) => {
        const idx = Number(key);
        if (!Number.isFinite(idx)) return;
        watchSidechain(idx);
        const sMap = sidechain.get(key);
        if (!(sMap instanceof Y.Map)) return;
        const eng = engineRef.current;
        if (!eng) return;
        const patch = {};
        sMap.forEach((value, field) => {
          if (field === "mode" || field === "keyChannel") patch[field] = value;
          else if (typeof value === "number") patch[field] = value;
        });
        eng._collabSilent = true;
        try { eng.setSidechain(idx, patch); }
        finally { eng._collabSilent = false; }
      });
    }
    function onSidechainKeyChange(event) {
      event.changes.keys.forEach((change, key) => {
        if (change.action === "add" || change.action === "update") {
          const idx = Number(key);
          if (!Number.isFinite(idx)) return;
          watchSidechain(idx);
          // Same atomic-add fix as onChannelsKeyChange — apply the snapshot
          // immediately so settings written in the creation transaction
          // aren't lost.
          const sMap = sidechain.get(key);
          if (!(sMap instanceof Y.Map)) return;
          const eng = engineRef.current;
          if (!eng) return;
          const patch = {};
          sMap.forEach((value, field) => {
            if (field === "mode" || field === "keyChannel") patch[field] = value;
            else if (typeof value === "number") patch[field] = value;
          });
          eng._collabSilent = true;
          try { eng.setSidechain(idx, patch); }
          finally { eng._collabSilent = false; }
        }
      });
    }
    sidechain.observe(onSidechainKeyChange);

    syncAllChannels();
    syncAllFx();
    syncAllSidechain();

    // Also re-sync once shortly after mount in case the initial full-state
    // arrives milliseconds after the bridge mounts. This is cheap and idempotent.
    const t0 = window.setTimeout(() => { syncAllChannels(); syncAllFx(); syncAllSidechain(); }, 250);
    const t1 = window.setTimeout(() => { syncAllChannels(); syncAllFx(); syncAllSidechain(); }, 1500);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      transport.unobserve(onTransportTx);
      channels.unobserve(onChannelsKeyChange);
      fx.unobserve(onFxKeyChange);
      sidechain.unobserve(onSidechainKeyChange);
      channelObservers.forEach((obs, idx) => {
        const chMap = channels.get(String(idx));
        if (chMap instanceof Y.Map) chMap.unobserve(obs);
      });
      channelObservers.clear();
      sidechainObservers.forEach((obs, idx) => {
        const sMap = sidechain.get(String(idx));
        if (sMap instanceof Y.Map) sMap.unobserve(obs);
      });
      sidechainObservers.clear();
    };
  }, [doc, ready]);
}
